// import React, { useState, useLayoutEffect } from "react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import {
  setScrollUp,
  setScrollDown,
  // resetScroll,
} from "../../app/actions/layout/scroll";
import { setWindowWidth } from "../../app/actions/layout/device";
import GlobalHeader from "./components/GlobalHeader";
import ActAsBanner from "../act-as-banner/ActAsBanner";
import DemoBlockedModal from "../demo/DemoBlockedModal";

// コンテンツ用のコンテナを追加
const ContentWrapper = styled.div`
  /* GlobalHeaderの高さ分のパディングを追加（isHideTopBarがfalseの場合のみ） */
  padding-top: ${props => props.$isHideTopBar ? "0" : (props.$scrollState === "down" ? "0" : "64px")};
  transition: padding-top 0.15s linear;
  position: relative; /* 子要素の位置決めの基準点 */
  z-index: 1; /* GlobalHeaderより下、他のコンテンツより上 */
`;

const GlobalWrapper = ({ children, isHideTopBar = false }) => {
  const dispatch = useDispatch();
  
  // スクロール状態を取得
  const scrollState = useSelector(state => state.layout);

  // デバイス状態（ウィンドウ状態）の管理
  useEffect(() => {
    // 初期ウィンドウ幅を設定
    dispatch(setWindowWidth(window.innerWidth));

    // リサイズイベントハンドラ
    const handleResize = () => {
      dispatch(setWindowWidth(window.innerWidth));
    };

    // リサイズイベントリスナーを追加
    window.addEventListener("resize", handleResize);

    // クリーンアップ関数
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    // スクロール状態を管理するための変数
    let isProcessingScroll = false;
    let lastScrollDirection = 'init';
    let scrollStabilityCounter = 0;
    const scrollStabilityThreshold = 5; // 閾値を5に増加（より安定したスクロール検出のため）
    let touchStartY = null;
    let lastScrollY = window.scrollY;
    let scrollTimer = null;
    let ticking = false;
    
    // スクロール状態を更新する関数
    const updateScrollState = (direction) => {
      // 同じ方向へのスクロールの場合
      if (lastScrollDirection === direction) {
        // 同じ方向なのでカウンターをリセット
        scrollStabilityCounter = 0;
        return;
      }
      
      // 方向が変わった場合、カウンターを増加
      scrollStabilityCounter++;
      
      // カウンターが閾値に達した場合のみ状態を更新
      if (scrollStabilityCounter >= scrollStabilityThreshold) {
        if (direction === 'up') {
          dispatch(setScrollUp());
        } else if (direction === 'down') {
          dispatch(setScrollDown());
        }
        
        // 状態を更新
        lastScrollDirection = direction;
        
        // カウンターをリセット
        scrollStabilityCounter = 0;
        
        // タイマーをクリア
        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }
        
        // スクロールが停止したら状態をリセットするタイマーを設定
        scrollTimer = setTimeout(() => {
        }, 200);
      }
    };
    
    // スクロールイベントハンドラ
    const handleScroll = (e) => {
      if (isProcessingScroll) return;
      
      isProcessingScroll = true;
      
      // ホイールイベントの場合
      if (e.type === 'wheel') {
        if (e.deltaY > 0) {
          updateScrollState('down');
        } else if (e.deltaY < 0) {
          updateScrollState('up');
        }
      }
      // タッチイベントの場合
      else if (e.type === 'touchmove') {
        // タッチスクロールの処理は別のハンドラで実装
      }
      
      // 処理フラグをリセット（スロットリング）
      setTimeout(() => {
        isProcessingScroll = false;
      }, 50); // 50msに統一（より敏感な反応）
    };

    // 高精度なスクロール検出のためのRAF（RequestAnimationFrame）を使用
    const detectScrollDirection = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const delta = Math.abs(currentScrollY - lastScrollY);
          
          // 小さな変化は無視（ブラウザの微小なスクロール位置の変化を防ぐ）
          if (delta >= 2) {
            if (currentScrollY > lastScrollY) {
              updateScrollState('down');
            } else if (currentScrollY < lastScrollY) {
              updateScrollState('up');
            }
            lastScrollY = currentScrollY;
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    const handleWheel = (event) => {
      // イベントのキャプチャを防止
      event.stopPropagation = false;
      
      const deltaY = event.deltaY;
      // 即座に反応するために閾値を小さくする
      if (deltaY > 0) {
        updateScrollState('down');
      } else if (deltaY < 0) {
        updateScrollState('up');
      }
      
      // スクロール方向の検出も実行
      detectScrollDirection();
    };

    const handleTouchStart = (event) => {
      touchStartY = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      if (touchStartY === null) return;
      const currentY = event.touches[0].clientY;
      // 即座に反応するために閾値を小さくする
      if (currentY < touchStartY - 5) { // 少し閾値を設定
        updateScrollState('down');
      } else if (currentY > touchStartY + 5) { // 少し閾値を設定
        updateScrollState('up');
      }
      touchStartY = currentY;
      
      // スクロール方向の検出も実行
      detectScrollDirection();
    };

    // マウス移動イベントを追加して、テキストエリア上でもスクロールを検出できるようにする
    const handleMouseMove = (event) => {
      // マウスの移動がスクロールと関連している場合に検出
      if (event.buttons === 4) { // マウスホイールボタンが押されている場合
        const movementY = event.movementY;
        if (movementY > 0) {
          updateScrollState('down');
        } else if (movementY < 0) {
          updateScrollState('up');
        }
      }
    };

    // テキストエリア内のスクロールを確実に検出するための関数
    const setupTextareaScrollDetection = () => {
      // すべてのテキストエリアを取得
      const textareas = document.querySelectorAll('textarea');
      
      // 各テキストエリアにイベントリスナーを追加
      textareas.forEach(textarea => {
        // すでにリスナーが追加されているかチェック
        if (!textarea.dataset.hasScrollListener) {
          // テキストエリア用のスクロール安定性カウンター
          let textareaScrollCounter = 0;
          
          // テキストエリアのスクロールイベントを処理する関数
          const handleTextareaScroll = (e) => {
            // スクロール方向に基づいて直接Reduxアクションをディスパッチ
            if (e.deltaY > 0) {
              // スクロールダウン - 安定性カウンターを使用
              if (lastScrollDirection === 'down') {
                // 同じ方向の場合はカウンターをリセット
                textareaScrollCounter = 0;
              } else {
                // 方向が変わった場合、カウンターを増加
                textareaScrollCounter++;
                
                // カウンターが閾値に達した場合のみディスパッチ
                if (textareaScrollCounter >= scrollStabilityThreshold) {
                  dispatch(setScrollDown());
                  
                  // 状態を更新（GlobalWrapperの状態管理と同期）
                  lastScrollDirection = 'down';
                  
                  // カウンターをリセット
                  textareaScrollCounter = 0;
                }
              }
            } else if (e.deltaY < 0) {
              // スクロールアップ - 安定性カウンターを使用
              if (lastScrollDirection === 'up') {
                // 同じ方向の場合はカウンターをリセット
                textareaScrollCounter = 0;
              } else {
                // 方向が変わった場合、カウンターを増加
                textareaScrollCounter++;
                
                // カウンターが閾値に達した場合のみディスパッチ
                if (textareaScrollCounter >= scrollStabilityThreshold) {
                  dispatch(setScrollUp());
                  
                  // 状態を更新（GlobalWrapperの状態管理と同期）
                  lastScrollDirection = 'up';
                  
                  // カウンターをリセット
                  textareaScrollCounter = 0;
                }
              }
            }
            
            // イベントの伝播を停止しない（重要）
            e.stopPropagation = false;
          };
          
          // キャプチャフェーズでイベントを捕捉（最優先で処理）
          textarea.addEventListener('wheel', handleTextareaScroll, { 
            passive: true, 
            capture: true 
          });
          
          // フォーカスイベントでもスクロール状態をリセット
          textarea.addEventListener('focus', () => {
            // フォーカス時の処理
          });
          
          // データ属性を設定してリスナーが追加されたことを記録
          textarea.dataset.hasScrollListener = 'true';
        }
      });
    };
    
    // 初期化時にテキストエリアのスクロール検出をセットアップ
    setupTextareaScrollDetection();
    
    // 定期的にテキストエリアのスクロール検出をセットアップ（動的に追加される要素のため）
    const intervalId = setInterval(setupTextareaScrollDetection, 1000);

    // イベントリスナーを高頻度で実行するように設定
    window.addEventListener("wheel", handleWheel, { passive: true, capture: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    // テキストエリアのスクロールイベントを直接監視する（MutationObserverの代わり）
    const documentWheelHandler = (e) => {
      // テキストエリア上でのスクロールを検出
      if (e.target.tagName === 'TEXTAREA') {
        if (e.deltaY > 0) {
          dispatch(setScrollDown());
        } else if (e.deltaY < 0) {
          dispatch(setScrollUp());
        }
      }
    };
    
    document.addEventListener('wheel', documentWheelHandler, { passive: true, capture: true });

    return () => {
      window.removeEventListener("wheel", handleWheel, { capture: true });
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      
      // テキストエリアなどからイベントリスナーを削除
      document.querySelectorAll('textarea').forEach(element => {
        element.removeEventListener('wheel', handleWheel, { capture: true });
        delete element.dataset.hasScrollListener;
      });
      
      // ドキュメントレベルのイベントリスナーを削除
      document.removeEventListener('wheel', documentWheelHandler, { capture: true });
      
      // インターバルをクリア
      clearInterval(intervalId);
      
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [dispatch]);

  return (
    <>
      <GlobalHeader isHideTopBar={isHideTopBar} />
      <ContentWrapper $scrollState={scrollState} $isHideTopBar={isHideTopBar}>
        {children}
      </ContentWrapper>
      {/* 代理操作 (act-as) 中のみ表示される固定バナー */}
      <ActAsBanner />
      {/* デモモード中の書き込み遮断モーダル（デモ表示バナーは置かない。
          デモ状態の解除は通常サインイン時の clearDemoMode で行われる） */}
      <DemoBlockedModal />
    </>
  );
};

export default GlobalWrapper;
